import React, { useEffect, useRef, useContext, useState } from 'react';
import Header from './Header';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { SearchRoutes, userRoutes, UpsellRoutes, AIAssistantRoutes, CmsAssistantRoutes} from '../Routes';
import { ProductContext } from '../store';
import DetailSkeleton from './Skeleton/DetailSkeleton';
import { getActiveWebsiteId } from '../utils/reportUtils';
import { isPlatformOverviewPath, isAccountSettingsPath, isUserActivityPath } from '../utils/adminRoutes';

const Content = ({type}) => {
	const [websitePlatform, setWebsitePlatform] = useState("")
	const {
		loadVisualEditor,
		userLoader,
		websiteLoader,
		currentWebsiteId,
		websiteData,
		userWebsitePlan,
		productTypes
		} = useContext(ProductContext);
	const mainContent	= useRef(null),
		location	= useLocation(),
		isPlatformOverview = isPlatformOverviewPath(location.pathname),
		isAccountSettings = isAccountSettingsPath(location.pathname),
		isUserActivity = isUserActivityPath(location.pathname);

	useEffect(() => {
		if (userLoader || websiteLoader || !currentWebsiteId) {
			return;
		}
		setWebsitePlatform(websiteData[currentWebsiteId]?.websiteType || "");
	}, [currentWebsiteId, userLoader, websiteLoader, websiteData]);

	const canRenderSearchContent =
		isPlatformOverview ||
		isAccountSettings ||
		isUserActivity ||
		(!userLoader && !websiteLoader && Boolean(getActiveWebsiteId(currentWebsiteId)));

	useEffect(() => {
		document.documentElement.scrollTop = 0;
		document.scrollingElement.scrollTop = 0;
		if( mainContent.current ){
			mainContent.current.scrollTop = 0;
		}
	}, [location]);

	const getRoutes = () => {
		let returnRoutes	= null
		try {
			if( type === "search" ){
				// Check if routes are available and is an array
				if (!SearchRoutes || !Array.isArray(SearchRoutes)) {
					console.error('SearchRoutes is not available or not an array');
					return null;
				}
				// Check if user has search product type
				returnRoutes = SearchRoutes.map((searchRoute, key) => {
				const isAdminOverviewRoute = searchRoute.path === '/admin-dashboard';
				const showSearchRoute =
					isAdminOverviewRoute ||
					searchRoute.component ||
					(searchRoute.subMenu && Array.isArray(searchRoute.subMenu));
				return (
					<React.Fragment key={key}>
						{showSearchRoute && (
							<React.Fragment>
								{searchRoute.component && (
									<Route
										exact
										path={searchRoute.path}
										element={searchRoute.component}
									/>
								)}
								{searchRoute.subMenu && Array.isArray(searchRoute.subMenu) &&
									searchRoute.subMenu.map((subMenu, index) => {
										if( ( !subMenu.availablePlatforms || ( subMenu.availablePlatforms && subMenu.availablePlatforms.includes(websitePlatform) ) ) && subMenu.component ){
											return (
												<Route
													exact
													path={subMenu.path}
													element={subMenu.component}
													key={index}
												/>
											)
										}
										return null;
									})
								}
								{key + 1 === SearchRoutes.length &&
									<Route path="*" element={<Navigate to="/search/admin-dashboard" replace />} />
								}
							</React.Fragment>
						)
					}
					</React.Fragment>
				);
			})
			} else if( type === "upsell" ){
				// Check if routes are available and is an array
				if (!UpsellRoutes || !Array.isArray(UpsellRoutes)) {
					console.error('UpsellRoutes is not available or not an array');
					return null;
				}
				returnRoutes = UpsellRoutes.map((upsellRoute, key) => {
				return (
					<React.Fragment key={key}>
						{(!upsellRoute.accessPlans || (upsellRoute.accessPlans && userWebsitePlan && Array.isArray(userWebsitePlan) && userWebsitePlan.some(plan => upsellRoute.accessPlans.includes(plan.planId)))) && (
							<React.Fragment>
								{upsellRoute.component && (
									<Route
										exact
										path={upsellRoute.path}
										element={upsellRoute.component}
									/>
								)}
								{upsellRoute.subMenu && Array.isArray(upsellRoute.subMenu) &&
									upsellRoute.subMenu.map((subMenu, index) => {
										if( ( !subMenu.availablePlatforms || ( subMenu.availablePlatforms && subMenu.availablePlatforms.includes(websitePlatform) ) ) && subMenu.component ){
											return (
												<Route
													exact
													path={subMenu.path}
													element={subMenu.component}
													key={index}
												/>
											)
										}
										return null;
									})
								}
								{key + 1 === UpsellRoutes.length &&
									<Route path="*" element={<Navigate to="/upsell/dynamic" replace />} />
								}
							</React.Fragment>
						)
					}
					</React.Fragment>
				);
			})
			} else if( type === "aiassistant" ){
				// Check if routes are available and is an array
				if (!AIAssistantRoutes || !Array.isArray(AIAssistantRoutes)) {
					console.error('AIAssistantRoutes is not available or not an array');
					return null;
				}
				const aiAdminOverview = userRoutes.find(
					(route) => route.path === '/admin-dashboard' && route.layout === '/aiassistant'
				);
				returnRoutes = [
					aiAdminOverview?.component ? (
						<Route
							key="ai-admin-dashboard"
							exact
							path="/admin-dashboard"
							element={aiAdminOverview.component}
						/>
					) : null,
					...AIAssistantRoutes.map((aiRoute, key) => {
				return (
					<React.Fragment key={key}>
						{(!aiRoute.accessPlans || (aiRoute.accessPlans && userWebsitePlan && Array.isArray(userWebsitePlan) && userWebsitePlan.some(plan => aiRoute.accessPlans.includes(plan.planId)))) && aiRoute.subMenu && Array.isArray(aiRoute.subMenu) && (
							<React.Fragment>
								{aiRoute.subMenu && Array.isArray(aiRoute.subMenu) &&
									aiRoute.subMenu.map((subMenu, index) => {
										if( ( !subMenu.availablePlatforms || ( subMenu.availablePlatforms && subMenu.availablePlatforms.includes(websitePlatform) ) ) && subMenu.component ){
											return (
												<Route
													exact
													path={subMenu.path}
													element={subMenu.component}
													key={index}
												/>
											)
										}
										return null;
									})
								}
							</React.Fragment>
						)
					}
					</React.Fragment>
				);
			}),
					<Route
						key="ai-fallback"
						path="*"
						element={<Navigate to="/aiassistant/admin-dashboard" replace />}
					/>,
				];
			} else {
				// Check if routes are available and is an array
				if (!userRoutes || !Array.isArray(userRoutes)) {
					console.error('userRoutes is not available or not an array');
					return null;
				}
				returnRoutes = userRoutes.map((prop, key) => {
										return (
											<>
												{prop.component && (
													<Route
														exact
														path={prop.path}
														element={prop.component}
														key={key}
													/>
												)}
												{key + 1 === userRoutes.length &&
													<Route path="*" element={<Navigate to="/user/dashboard" replace />} />
												}
											</>
										);
									})
			}
		} catch (error) {
			console.error('Error in getRoutes:', error);
			return null;
		}
		return returnRoutes
	}
	return (
		<div className={`contentMainContainer ${isPlatformOverview ? 'platformOverviewContent' : ''}`} id="mainContentScroll" ref={mainContent}>
			<Header hideWebsiteDropdown={isPlatformOverview} />
			<div className={loadVisualEditor?'pageContentWrapperNoPadding':'pageContentWrapper'}>
				<div className={`pageContentInnerWrapper ${isPlatformOverview ? 'platformOverviewInner' : ''}`}>
					{isPlatformOverview || (type === "search" || type === "aiassistant" ? canRenderSearchContent : (!userLoader && userWebsitePlan && Array.isArray(userWebsitePlan) && userWebsitePlan.length > 0)) ? (
						<Routes>
							{getRoutes()}
						</Routes>
					) : (
						<DetailSkeleton />
					)}
				</div>
			</div>
		</div>
	);
}

export default Content;